import $get from 'lodash.get'
import $merge from 'lodash.merge'
import localstore from 'store'

import {
  RESET_INITIAL_STATE,
  SET_AUTHENTICATED,
  SET_LOADER,
  SET_PASSWORD_ASKED,
  SET_USER,
} from './types'
import notifier from '~/utils/notifier'
import spoke, { $metadata, $protected } from '~/utils/spoke'
import {
  getConfig,
  hasLocalAuthModeUniquePassword,
  runExcentricFile,
} from '~/utils/voicer'

export const namespace = true

const excentrics = runExcentricFile('store', 'auth')

const DEFAULT_STATE = () => {
  return {
    /**
     * @type {Boolean}
     */
    isAuthenticated: false,

    /**
     * @type {Boolean}
     */
    isLoading: true,

    /**
     * @type {Boolean}
     */
    hasAskedPassword: false,

    /**
     * @type {Object}
     */
    user: spoke.user.toJSON(),
  }
}

export const state = () => ({
  ...$merge(DEFAULT_STATE(), excentrics.state),
})

export const actions = $merge(
  {
    /**
     * attempt to ask a password reset for a given `login`
     * @param {String} login user login
     * @returns {Promise}
     */
    async askNewPassword({ dispatch, commit }, login) {
      const notify = notifier(dispatch)
      commit(SET_LOADER, true)

      try {
        await spoke.authentication.askNewPassword(login)
        commit(SET_PASSWORD_ASKED, true)
        notify('success', this.app.i18n.t('st_auth_asknewpassword_success'))
      } catch (error) {
        notify('error', this.app.i18n.t('st_auth_asknewpassword_err'))
      } finally {
        commit(SET_LOADER, false)
      }
    },

    /**
     * authenticate current spoke session regardings
     * passed `payload`
     * @param {Object} payload the credential object
     * @param {String} payload.login
     * @param {String} payload.password
     * @returns {Promise}
     *
     * @todo
     * intercept spoke errors (based on `error.status`)
     * and dispatch this informations to provide theses
     * informations on the ui
     * eg: 401: bad credentials
     * eg: 404: something is wrong with this site
     * eg: 50*: something is fucking wrong with the brocoli api...
     *
     * in the case where error.status is undefined, error
     * is related to spoke or come from the client side
     * implementation, treat this error as a 500 error
     *
     * note: !status or status > 403 should submit a notice
     * on logrocket, logly or a remote debugger
     */
    async authenticate({ dispatch, commit, redirect = true }, payload) {
      const notify = notifier(dispatch)
      commit(SET_LOADER)

      try {
        await spoke.authenticate(payload)
        const user = spoke.user.lean()

        commit(SET_USER, user)
        commit(SET_AUTHENTICATED, true)

        dispatch('notifyWelcome', user)

        if (redirect === true) {
          this.$router.replace('/')
        }
      } catch (error) {
        notify('error', error, this.app.i18n.t('st_auth_authenticate_err'))

        throw error // propagate error
      } finally {
        commit(SET_LOADER, false)
      }
    },

    async getAuthReady({ dispatch, commit }) {},

    async authenticateWithApiRoute(
      { dispatch, commit },
      {
        apiRoute,
        payload,
        redirect = true,
        errorKey = 'st_auth_authenticatewithprovider_err',
      }
    ) {
      const notify = notifier(dispatch)
      commit(SET_LOADER)

      if ((await spoke.user.session.hasValidSession()) === false) {
        await spoke.user.session.create()
      }

      try {
        const { item: user } = await spoke.http.post(apiRoute, payload)
        spoke.doRealtimeConnection(spoke.user.session.token)

        spoke.user.$rehydrate(user)
        spoke.user.isAuthenticated = true

        commit(SET_USER, user)
        commit(SET_AUTHENTICATED, true)

        dispatch('notifyWelcome', user)
        if (redirect === true) {
          this.$router.replace('/')
        }
      } catch (error) {
        notify('error', error, this.app.i18n.t(errorKey))
        throw error
      } finally {
        commit(SET_LOADER, false)
      }
    },

    authenticateMagicLink({ dispatch }, payload) {
      return dispatch('authenticateWithApiRoute', {
        apiRoute: '/users/magic_link/validate',
        payload,
        redirect: true,
        errorKey: 'st_auth_authenticatemagiclink_err',
      })
    },

    authenticateWithProvider(
      { dispatch },
      { brocoliAPIRoute, payload, redirect = true }
    ) {
      const ssoConfig = localstore.get('lastUsedSSO')
      if (!ssoConfig || !ssoConfig.clientId) {
        console.error('No info about which SSO was used')
        throw new Error('Could not perform authentication')
      }
      payload.clientId = ssoConfig.clientId
      return dispatch('authenticateWithApiRoute', {
        apiRoute: brocoliAPIRoute,
        payload,
        redirect,
        errorKey: 'st_auth_authenticatewithprovider_err',
      })
    },

    authenticateKeycloakProvider({ dispatch }, payload) {
      dispatch('authenticateWithProvider', {
        brocoliAPIRoute: 'users/keycloak/authenticate',
        payload,
        redirect: true,
      })
    },

    notifyWelcome(context, user) {
      const { dispatch, state } = context

      if (!user) {
        user = state.user || {}
      }

      const notify = notifier(dispatch)
      const trkey =
        hasLocalAuthModeUniquePassword() || user.isAnonymous
          ? 'st_auth_notifywelcome_anonymous'
          : 'st_auth_notifywelcome_identified'

      notify(
        'success',
        this.app.i18n.t(trkey, {
          appname: this.app.$metadatas.appName,
          name: $get(user, this.$voicer.getConfig('models.user.name')),
        })
      )
    },

    async otpCreate(context, payload) {
      const { commit, dispatch } = context
      const notify = notifier(dispatch)

      commit(SET_LOADER)

      if ((await spoke.user.session.hasValidSession()) === false) {
        await spoke.user.session.create()
      }

      const internalSendOTP = async (clearSession = false) => {
        if (clearSession === true) {
          // clear prev session and create a new one
          await spoke.user.session.create()
        }

        // force new creation
        await spoke.http.post('/users/otp/create', payload)
        notify('success', this.app.i18n.t('st_auth_otpcreate_success'))
        commit(SET_LOADER, false)
      }

      try {
        await internalSendOTP()
      } catch (error) {
        if (
          error.status === 400 &&
          error.response.data.error === 'FLOOD_PREVENTION'
        ) {
          notify('error', this.app.i18n.t('st_auth_otpcreate_errflood'))
          throw error
        } else {
          // retry attempt
          return new Promise((resolve, reject) => {
            internalSendOTP(true)
              .then(() => {
                resolve()
                commit(SET_LOADER, false)
              })
              .catch((error) => {
                notify('error', this.app.i18n.t('st_auth_otpcreate_errunknown'))
                reject(error)
                commit(SET_LOADER, false)
              })
          })
        }
      }
    },

    async otpValidate(context, payload) {
      const { commit, dispatch } = context
      const notify = notifier(dispatch)

      commit(SET_LOADER)

      try {
        const { item: user } = await spoke.http.post(
          '/users/otp/validate',
          payload
        )
        dispatch('notifyWelcome', user)
        spoke.doRealtimeConnection(spoke.user.session.token)

        spoke.user.$rehydrate(user)
        spoke.user.isAuthenticated = true

        commit(SET_USER, user)
        commit(SET_AUTHENTICATED, true)
        commit(SET_LOADER, false)
        this.$router.replace('/')
      } catch (error) {
        if (
          error.status === 403 &&
          error.response.data.error.explicitMessage ===
            'post_users_activate__max_tries'
        ) {
          notify('error', this.app.i18n.t('st_auth_otpvalidate_errmaxtries'))
        } else {
          console.error(error)
          notify('error', this.app.i18n.t('st_auth_otpvalidate_errwrong'))
        }

        commit(SET_LOADER, false)
        throw error
      }
    },

    /**
     * logout current user
     * under the hood, will revoke the current user
     * session and create a new one before redirect
     * user on the homepage
     * should reset all the store on an INITIAL STATE
     * @returns {Promise}
     */
    async logout({ dispatch, commit }) {
      const notify = notifier(dispatch)
      commit(SET_LOADER)

      const sleep = (ms) =>
        new Promise((resolve) => {
          setTimeout(resolve, ms)
        })

      try {
        await spoke.logout()
        notify('success', this.app.i18n.t('st_auth_logout_success'))
        await sleep(500)
        // fuck this !
        // uncatched error ....
        // dispatch('resetState')
        // dispatch('player/resetState', null, { root: true })
        // dispatch('comments/resetState', null, { root: true })
        // dispatch('contents/resetState', null, { root: true })
        // dispatch('filters/resetState', null, { root: true })
      } catch (error) {
        // noop
        // it's probably a network issue (or API issue 🤪)
        // spoke can't regenerate e clean session for this user
      } finally {
        commit(RESET_INITIAL_STATE)
        commit(SET_LOADER, false)
        window.location.href = '/login?from=logout'
      }
    },

    /**
     * registration process
     *
     * will attach user session on this registration
     * @todo
     * intercept spoke registration errors
     * eg: 400: bad payload
     * eg: 422: duplicate login
     * eg: 500: api error...
     *
     * @param {Object} payload registration object
     * @param {String} payload.login basically, customer email
     * @param {String} payload.password
     * @param {String} payload.username
     * @returns {Promise}
     */
    async register({ dispatch, commit }, payload) {
      const notify = notifier(dispatch)
      commit(SET_LOADER)

      try {
        const data = await spoke.register().create({
          login: payload.login,
          password: payload.password,
          metadatas: {
            name: payload.username,
            email: payload.login,
          },
        })

        commit(SET_LOADER, false)
        notify('success', this.app.i18n.t('st_auth_register_success'))

        return data
      } catch (error) {
        if (error && error.status) {
          switch (error.status) {
            case 400:
              notify('error', this.app.i18n.t('st_auth_register_err400'))
              break
            case 403:
              notify('error', this.app.i18n.t('st_auth_register_err403'))
              break
            case 409:
              notify('error', this.app.i18n.t('st_auth_register_err409'))
              break
            default:
              notify('error', null, error)
              break
          }
        }

        commit(SET_LOADER, false)
        // error should propaged
        return Promise.reject(error)
      }
    },

    /**
     * attempt to reset an user password with informations
     * sent via Email/SMS (see payload)
     * @param {Object} payload credential object
     * @param {String} payload.code temp code (sent via SMS/email...)
     * @param {String} payload.password user new password
     * @param {String} payload.userId user ObjectID (mongo id)
     * @returns {Promise}
     *
     * @todo
     * properly intercept API errors (see status code)
     */
    async resetPassword({ dispatch, commit }, payload) {
      const notify = notifier(dispatch)
      commit(SET_LOADER)

      try {
        await spoke.authentication.resetPassword(
          payload.userId,
          payload.code,
          payload.password
        )

        this.$router.push('/login')

        notify('success', this.app.i18n.t(`st_auth_resetpassword_success`))
      } catch (error) {
        notify('error', this.app.i18n.t(`st_auth_resetpassword_err`))
      } finally {
        commit(SET_LOADER, false)
      }
    },

    resetState({ commit }) {
      commit(RESET_INITIAL_STATE)
    },

    async activateAccount({ dispatch, commit }, { uid, code }) {
      const notify = notifier(dispatch)
      try {
        if (!this.$spoke.registration) {
          this.$spoke.register().resurrect(uid)
        }

        await this.$spoke.registration.validateCode(code)
        notify('success', this.app.i18n.t('st_auth_activateaccount_success'))
      } catch (error) {
        notify('error', this.app.i18n.t('st_auth_activateaccount_err'))
        throw error
      }
    },
  },
  excentrics.actions
)

export const mutations = $merge(
  {
    /**
     * true if user is authenticated
     */
    [SET_AUTHENTICATED](state, isAuthenticated = true) {
      state.isAuthenticated = isAuthenticated
    },

    /**
     * true if something an authentication / registration
     * process is loading (pending API response)
     */
    [SET_LOADER](state, isLoading = true) {
      state.isLoading = isLoading
    },

    /**
     * if the user has asked password and API confirmed mail was sent
     */
    [SET_PASSWORD_ASKED](state) {
      state.hasAskedPassword = true
    },

    /**
     * set leanified spoke.user object in vuex store
     */
    [SET_USER](state, user) {
      state.user = user
    },

    /**
     * reset vuex auth store on initial state
     */
    [RESET_INITIAL_STATE](state) {
      Object.assign(state, DEFAULT_STATE())
    },
  },
  excentrics.mutations
)

export const getters = $merge(
  {
    userChars(state) {
      const name = $metadata(state.user, 'name', 'Anonyme')

      const sname = name.split(' ')

      if (sname.length >= 2) {
        return sname[0].charAt(0) + sname[1].charAt(0)
      } else if (sname.length === 1) {
        return sname[0].charAt(0)
      }
      return 'A'
    },
    userId(state) {
      return state.user.id
    },
    email(state) {
      return $protected(state.user, 'email')
    },
    name(state) {
      return $get(state.user, getConfig('models.user.name'))
    },
    isAuthenticated(state) {
      return spoke.site.optAuthentication === false || state.isAuthenticated
    },
  },
  excentrics.getters
)
